/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTermStructureConfig } from '../../../hooks/iVol/useTermStructureConfig';
import { useVolSkewConfig } from '../../../hooks/iVol/useVolSkewConfig';
import { HistoryTable } from '../EquityhubTable/HistoryTable';
import { SkewGraph } from './SkewGraph';
import { PutCallImpactGraph } from './PutCallImpactGraph';
import { SynthOIPutCallImpactGraph } from './SynthOIPutCallImpactGraph';
import { CompositeViewGraph } from './CompositeViewGraph';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  liveSgChartDataState,
  realTimeChartZoomConfigState,
  rrChartZoomConfigState,
  rrInitialDataState,
  selectedEquitySymbolState,
  selectedEquityState,
  userDetailsState,
  synthOILiveChartTypeState,
} from '../../../states';
import { InfoButton, SGSelect, UpsellModal, ZoomOutButton } from '../../shared';
import { RiskReversalGraph } from './RiskReversalGraph';
import { getSubscriptionLevel, updateSearch } from '../../../util';
import { SGTooltip } from '../../core';
import { SymbolTabs } from '../../hiro/HiroChart/controls/SymbolTabs';
import { HiroProvider } from 'components/hiro/HiroProvider';
import { DEFAULT_BRUSH_ZOOM_CONFIG } from '../../../config';
import { IVOL_UPSELL } from '../../../config/iVol';
import useAuth from '../../../hooks/auth/useAuth';
import {
  EhCompositeMode,
  IVolTab,
  ProductType,
  TOTAL_OI_TAB_KEYS,
  SYNTH_OI_TAB_KEYS,
  SubLevel,
  SynthOIChartType,
  EH_TOTAL_OI_ORDERED_TABS,
  EH_SYNTH_OI_ORDERED_TABS,
  SynthOILiveChartTypeLabels,
  EquityHubTab,
} from '../../../types';
import {
  FixedStrikeTable,
  FixedStrikeProvider,
} from '../../implied_vol/FixedStrikeMatrix';
import { SynthOIPutCallImpactSettings } from './SynthOIPutCallImpactSettings';
import { TotalOIPutCallImpactSettings } from './TotalOIPutCallImpactSettings';
import { SynthOIPutCallImpactProvider } from './SynthOIPutCallImpactContext';
import { SynthOIPutCallImpactZoomOutButton } from './SynthOIPutCallImpactZoomOutButton';
import { TotalOIPutCallImpactProvider } from './TotalOIPutCallImpactContext';
import { TotalOIPutCallImpactZoomOutButton } from './TotalOIPutCallImpactZoomOutButton';
import { useSynthOIPutCallImpactConfig } from '../../../hooks/putCallImpact/useSynthOIPutCallImpactConfig';
import { useTotalOIPutCallImpactConfig } from '../../../hooks/putCallImpact/useTotalOIPutCallImpactConfig';
import { useCompositeViewConfig } from '../../../hooks/compositeView/useCompositeViewConfig';
import { CompositeViewProvider } from './CompositeViewProvider';
import { useCompositeViewCtx } from './useCompositeViewCtx';
import { FixedStrikeTableSettings } from '../../implied_vol/FixedStrikeMatrix/FixedStrikeTableControls';
import {
  TermStructureChartSettings,
  TermStructureProvider,
  TermStructureZoomOutButton,
  TermStructureDownloadButton,
} from '../../implied_vol/TermStructure';
import TermStructureChart from '../../implied_vol/TermStructure/TermStructureChart';
import {
  VolSkewChartSettings,
  VolSkewZoomOutButton,
  VolSkewDownloadButton,
  VolSkewProvider,
} from '../../implied_vol/VolSkew';
import VolSkewChart from '../../implied_vol/VolSkew/VolSkewChart';

import PanelFullscreenButton from 'components/shared/PanelFullscreenButton';
import TabbedContent from 'components/shared/TabbedContent';
import { TabItem } from 'types/shared';
import { LivePriceAndSgLevels } from './LivePriceAndSgLevels';
import { RealTimeGraph } from './RealTimeGraph';
import useSynthOi from 'hooks/equityhub/useSynthOi';
import { useFixedStrikeConfig } from 'hooks/iVol/useFixedStrikeConfig';
import { usePersistedState } from 'hooks/usePersistedState';

const ivolChartTabs = [
  IVolTab.TermStructure as string,
  IVolTab.VolSkew as string,
];

const ivolTabs = [IVolTab.FixedStrikeMatrix as string, ...ivolChartTabs];

const greekTypeTextTransform = (value: SynthOIChartType) => {
  return SynthOILiveChartTypeLabels.get(value) || '';
};

const COMPOSITE_LABELS = new Map([
  [EhCompositeMode.Gamma, 'Gamma'],
  [EhCompositeMode.Factors, 'Impact'],
]);

const compositeTypeTextTransform = (value: EhCompositeMode) => {
  return COMPOSITE_LABELS.get(value) || '';
};

const CompositeKeyLevelsToggle = () => {
  const { config, updateConfig } = useCompositeViewCtx();
  return (
    <FormControlLabel
      sx={{ width: '120px', marginLeft: '4px' }}
      control={
        <Switch
          checked={config.showKeyLevels}
          onChange={(_e, checked: boolean) =>
            updateConfig({ showKeyLevels: checked })
          }
        />
      }
      label={<Typography sx={{ fontSize: 13 }}>Show key levels</Typography>}
      labelPlacement="end"
    />
  );
};

export const EquityGraph = () => {
  const [upsellOpen, setUpsellOpen] = useState<boolean>(false);
  const [zoomedIn, setZoomedIn] = useState<boolean>(false);
  const [searchParams, setSearch] = useSearchParams();
  const userDetails = useRecoilValue(userDetailsState);

  const [fsConfig, updateFsConfig] = useFixedStrikeConfig('eh_fs');

  // Term Structure config via nuqs URL state
  const [tsConfig, updateTsConfig] = useTermStructureConfig();

  const { isSynthOI } = useSynthOi();

  const { hasAccessToProduct } = useAuth();
  const hasImpliedVolAccess = hasAccessToProduct(ProductType.IMPLIED_VOL);
  const membership = useMemo(
    () => getSubscriptionLevel(userDetails),
    [userDetails],
  );

  const currentTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return tab ?? (isSynthOI ? 'pc_impact' : 'composite');
  }, [searchParams, isSynthOI]);

  useEffect(() => {
    if (
      currentTab == null ||
      (!TOTAL_OI_TAB_KEYS.has(currentTab) && !SYNTH_OI_TAB_KEYS.has(currentTab))
    ) {
      setSearch(updateSearch({ tab: isSynthOI ? 'pc_impact' : 'composite' }));
    }
  }, [currentTab, setSearch]);

  useEffect(() => {
    if (ivolTabs.includes(currentTab) && !hasImpliedVolAccess) {
      setUpsellOpen(true);
    }
  }, [currentTab, hasImpliedVolAccess]);

  const handleChange = (tab: string) => {
    if (ivolTabs.includes(tab) && !hasImpliedVolAccess) {
      setUpsellOpen(true);
    } else {
      setZoomedIn(false);
      setSearch(updateSearch({ tab }));
    }
  };

  const selectedEquity = useRecoilValue(selectedEquityState);
  const selectedSymbol = useRecoilValue(selectedEquitySymbolState);
  const [showKeyLevels, setShowKeyLevels] = useState(true);
  const isMobile = useIsMobile();
  const [realTimeZoomConfig, setRealTimeZoomConfig] = useRecoilState(
    realTimeChartZoomConfigState,
  );
  const realTimeInitialData = useRecoilValue(liveSgChartDataState);
  const [rrImpactZoomConfig, setRRImpactZoomConfig] = useRecoilState(
    rrChartZoomConfigState,
  );
  const rrImpactInitialData = useRecoilValue(rrInitialDataState);

  const [liveChartType, setLiveChartType] = useRecoilState(
    synthOILiveChartTypeState,
  );

  // Put/Call Impact configs via nuqs URL state
  const [synthOIPCIConfig, updateSynthOIPCIConfig] =
    useSynthOIPutCallImpactConfig();
  const [totalOIPCIConfig, updateTotalOIPCIConfig] =
    useTotalOIPutCallImpactConfig();

  // Vol Skew config via nuqs URL state
  const [vsConfig, updateVsConfig] = useVolSkewConfig();

  // Composite View config via nuqs URL state
  const [compositeConfig, updateCompositeConfig] = useCompositeViewConfig();

  // Persist all EH chart configs to localStorage across navigations
  const allEhConfigs = useMemo(
    () => ({
      synthOI: synthOIPCIConfig,
      totalOI: totalOIPCIConfig,
      composite: compositeConfig,
    }),
    [synthOIPCIConfig, totalOIPCIConfig, compositeConfig],
  );

  const restoreEhConfigs = useCallback(
    (saved: typeof allEhConfigs) => {
      if (saved.synthOI) {
        updateSynthOIPCIConfig(saved.synthOI);
      }
      if (saved.totalOI) {
        updateTotalOIPCIConfig(saved.totalOI);
      }
      if (saved.composite) {
        updateCompositeConfig(saved.composite);
      }
    },
    [updateSynthOIPCIConfig, updateTotalOIPCIConfig, updateCompositeConfig],
  );

  usePersistedState('eh_chart_query_state', allEhConfigs, restoreEhConfigs, [
    'spci',
    'tpci',
    'cv',
  ]);

  const zoomButton = useMemo(() => {
    switch (currentTab) {
      case 'live_price':
        return !isSynthOI ? (
          <ZoomOutButton
            zoomConfig={realTimeZoomConfig}
            setZoomConfig={setRealTimeZoomConfig}
            initialData={realTimeInitialData}
            overrideDefault={{
              leftIdx: DEFAULT_BRUSH_ZOOM_CONFIG.leftIdx,
              rightIdx: realTimeInitialData.length - 1,
            }}
          />
        ) : null;
      case 'pc_impact':
        return isSynthOI ? (
          <SynthOIPutCallImpactZoomOutButton key="zoom" />
        ) : (
          <TotalOIPutCallImpactZoomOutButton key="zoom" />
        );
      case 'rr':
        return (
          <ZoomOutButton
            zoomConfig={rrImpactZoomConfig}
            setZoomConfig={setRRImpactZoomConfig}
            initialData={rrImpactInitialData}
            overrideDefault={{
              leftIdx: DEFAULT_BRUSH_ZOOM_CONFIG.leftIdx,
              rightIdx: rrImpactInitialData.length - 1,
            }}
          />
        );
      case IVolTab.TermStructure:
        return <TermStructureZoomOutButton key="zoom" />;
      case IVolTab.VolSkew:
        return <VolSkewZoomOutButton key="zoom" />;
      default:
        return null;
    }
  }, [
    currentTab,
    realTimeZoomConfig,
    setRealTimeZoomConfig,
    realTimeInitialData,
    rrImpactZoomConfig,
    setRRImpactZoomConfig,
    rrImpactInitialData,
    isSynthOI,
  ]);

  const settingsBtn = useMemo(() => {
    switch (currentTab) {
      case 'pc_impact':
        return isSynthOI ? (
          <SynthOIPutCallImpactSettings key="synth-oi-pc-impact-settings" />
        ) : (
          <TotalOIPutCallImpactSettings key="total-oi-pc-impact-settings" />
        );
      case IVolTab.FixedStrikeMatrix:
        return <FixedStrikeTableSettings key="fixed-strike-table-settings" />;
      case IVolTab.TermStructure:
        return <TermStructureChartSettings key="term-structure-settings" />;
      case IVolTab.VolSkew:
        return <VolSkewChartSettings key="volatility-skew-settings" />;
      default:
        return null;
    }
  }, [currentTab, isSynthOI]);

  const hasFactors = useMemo(() => {
    return isSynthOI && !isNaN(selectedEquity?.position_factor ?? NaN);
  }, [isSynthOI, selectedEquity]);

  useEffect(() => {
    if (isSynthOI && selectedEquity != null && !hasFactors) {
      // Force Gamma mode when we can't support position_factor
      updateCompositeConfig({ compositeMode: EhCompositeMode.Gamma });
    }
  }, [isSynthOI, selectedEquity, hasFactors]);

  const downloadBtn = useMemo(() => {
    switch (currentTab) {
      case IVolTab.TermStructure:
        return <TermStructureDownloadButton />;
      case IVolTab.VolSkew:
        return <VolSkewDownloadButton />;
      default:
        return null;
    }
  }, [currentTab]);

  const getTabActions = useCallback(
    (tabKey: string) => {
      const actions = [];

      if (
        !['skew', 'history', 'pc_impact', 'composite', ...ivolTabs].includes(
          tabKey,
        )
      ) {
        actions.push(
          <FormControlLabel
            sx={{
              width: '120px',
              marginLeft: !isMobile ? '4px' : 0,
            }}
            control={
              <Switch
                checked={showKeyLevels}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setShowKeyLevels(event.target.checked)
                }
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: isMobile ? 11 : 13,
                }}
              >
                Show key levels
              </Typography>
            }
            labelPlacement={isMobile ? 'top' : 'end'}
          />,
        );
      }

      if (tabKey === 'composite') {
        actions.push(<CompositeKeyLevelsToggle key="composite-key-levels" />);
      }

      if (tabKey === 'composite' && isSynthOI) {
        actions.push(
          <SGSelect
            key="composite-mode-selector"
            label="Chart Type"
            value={compositeConfig.compositeMode}
            disabled={!hasFactors}
            setter={(val: EhCompositeMode) =>
              updateCompositeConfig({ compositeMode: val })
            }
            options={[EhCompositeMode.Gamma, EhCompositeMode.Factors]}
            multiple={false}
            optionsTextTransform={compositeTypeTextTransform}
            sx={{ minWidth: 120, fontSize: '0.8rem' }}
          />,
        );
      }

      if (tabKey === 'live_price' && isSynthOI) {
        actions.push(
          <SGSelect
            key="greek-type-selector"
            label="Greek Type"
            value={liveChartType}
            setter={setLiveChartType}
            options={[
              SynthOIChartType.Gamma,
              SynthOIChartType.Delta,
              SynthOIChartType.OpenInterest,
              SynthOIChartType.NetPositioning,
            ]}
            multiple={false}
            optionsTextTransform={greekTypeTextTransform}
            sx={{ minWidth: 120, fontSize: '0.8rem' }}
          />,
        );
      }

      if (!['history'].includes(tabKey)) {
        actions.push(
          <InfoButton
            key={`${tabKey}-info`}
            articleKey={isSynthOI ? `${tabKey}_synthoi` : tabKey}
          />,
        );
      }

      if (ivolChartTabs.includes(tabKey)) {
        actions.push(downloadBtn);
      }

      const zoomButtonElement =
        zoomedIn || !zoomButton ? (
          zoomButton
        ) : (
          <SGTooltip title="Drag on the chart to zoom in" placement="top">
            {zoomButton}
          </SGTooltip>
        );

      if (zoomButtonElement) {
        actions.push(zoomButtonElement);
      }

      return actions.filter(Boolean);
    },
    [
      compositeConfig,
      updateCompositeConfig,
      downloadBtn,
      hasFactors,
      isMobile,
      isSynthOI,
      liveChartType,
      setLiveChartType,
      setShowKeyLevels,
      showKeyLevels,
      zoomButton,
      zoomedIn,
    ],
  );

  const staticActions = [
    settingsBtn,
    <PanelFullscreenButton panelType="main" />,
  ].filter(Boolean);

  const filteredTABS = Object.keys(
    isSynthOI ? EH_SYNTH_OI_ORDERED_TABS : EH_TOTAL_OI_ORDERED_TABS,
  )
    .filter((key) => !(membership === SubLevel.ALPHA && key === 'skew'))
    .reduce(
      (obj, key) => {
        obj[key] = isSynthOI
          ? (EH_SYNTH_OI_ORDERED_TABS[key as EquityHubTab] ?? '')
          : (EH_TOTAL_OI_ORDERED_TABS[key as EquityHubTab] ?? '');
        return obj;
      },
      {} as { [key: string]: string },
    );

  const tabs: TabItem[] = useMemo(
    () =>
      Object.entries(filteredTABS).map(([key, label]) => ({
        label,
        value: key,
        content: (
          <>
            {key === 'composite' && <CompositeViewGraph />}
            {key === 'live_price' &&
              (isSynthOI ? (
                <LivePriceAndSgLevels showKeyLevels={showKeyLevels} />
              ) : (
                <RealTimeGraph showKeyLevels={showKeyLevels} />
              ))}
            {key === 'skew' && <SkewGraph />}
            {key === 'pc_impact' &&
              (isSynthOI ? (
                <SynthOIPutCallImpactGraph />
              ) : (
                <PutCallImpactGraph />
              ))}
            {key === 'rr' && <RiskReversalGraph />}
            {key === IVolTab.FixedStrikeMatrix && <FixedStrikeTable />}
            {key === IVolTab.TermStructure && <TermStructureChart />}
            {key === IVolTab.VolSkew && <VolSkewChart />}
            {key === 'history' && <HistoryTable />}
          </>
        ),
        actions: getTabActions(key),
      })),
    [filteredTABS, isSynthOI, showKeyLevels, selectedSymbol, getTabActions],
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <FixedStrikeProvider
        config={{ ...fsConfig, sym: selectedSymbol }}
        onConfigChange={updateFsConfig}
      >
        <TermStructureProvider
          selectedSym={selectedSymbol ?? ''}
          config={tsConfig}
          onConfigChange={updateTsConfig}
        >
          <VolSkewProvider
            selectedSym={selectedSymbol ?? ''}
            config={vsConfig}
            onConfigChange={updateVsConfig}
          >
            <SynthOIPutCallImpactProvider
              selectedSym={selectedSymbol ?? ''}
              config={synthOIPCIConfig}
              onConfigChange={updateSynthOIPCIConfig}
            >
              <TotalOIPutCallImpactProvider
                selectedSym={selectedSymbol ?? ''}
                config={totalOIPCIConfig}
                onConfigChange={updateTotalOIPCIConfig}
              >
                <CompositeViewProvider
                  selectedSym={selectedSymbol ?? ''}
                  isSynthOI={isSynthOI}
                  config={compositeConfig}
                  onConfigChange={updateCompositeConfig}
                >
                  <TabbedContent
                    currentSym={selectedSymbol}
                    tabs={tabs}
                    subTabsComponent={
                      <HiroProvider mode="standalone">
                        <SymbolTabs productType={ProductType.EQUITYHUB} />
                      </HiroProvider>
                    }
                    defaultTab={currentTab}
                    staticActions={staticActions}
                    onChange={handleChange}
                  />
                </CompositeViewProvider>
              </TotalOIPutCallImpactProvider>
            </SynthOIPutCallImpactProvider>
          </VolSkewProvider>
        </TermStructureProvider>
      </FixedStrikeProvider>

      <UpsellModal
        open={upsellOpen}
        setOpen={setUpsellOpen}
        title={IVOL_UPSELL.title}
        subtitle={IVOL_UPSELL.subtitle}
        items={IVOL_UPSELL.items}
      />
    </Box>
  );
};
